/* eslint-disable react-refresh/only-export-components */
import { createContext, useEffect, useState, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService, AUTH_TOKEN_CHANGE_EVENT, getImpersonatorId } from '@/services/auth';
import api, { clearAllUserScopedCaches } from '@/services/api';
import type { AuthContextType, AuthState, User } from '@/types';
import { debugEvent } from '@/lib/debugApi';

const IMPERSONATION_ORIGINAL_TOKEN_KEY = 'admin_original_token';
const IMPERSONATION_ORIGINAL_USER_KEY = 'admin_original_user';
const IMPERSONATION_RETURN_URL_KEY = 'admin_return_url';

// Token refresh constants
const REFRESH_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const REFRESH_DEBOUNCE_MS = 5000; // 5 seconds

const USER_SCOPED_QUERY_ROOTS = new Set([
  'creditHistory',
  'copyquestion-presets',
  'examQuestions',
  'lms-export-presets',
  'make-questions-presets',
  'passage-categories',
  'passages',
  'questions',
  'sheets',
  'usedFiles',
  'workbook',
  'workbooks',
]);

const initialState: AuthState = {
  user: null,
  token: null,
  credits: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isImpersonating: false,
  originalToken: null,
};

export const AuthContext = createContext<AuthContextType>({
  state: initialState,
  user: null,
  token: null,
  credits: null,
  isImpersonating: false,
  login: async () => {},
  logout: () => {},
  refreshCredits: async () => {},
  refreshUser: async () => {},
  updateCredits: () => {},
  startImpersonation: async () => {},
  stopImpersonation: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

function isUserScopedQuery(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0];
  return typeof root === 'string' && USER_SCOPED_QUERY_ROOTS.has(root);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Detect impersonation from JWT token (works across tabs and page refreshes)
  const initialToken = typeof window !== 'undefined' ? authService.getToken() : null;
  const restoredIsImpersonating = initialToken ? getImpersonatorId(initialToken) != null : false;
  const restoredOriginalToken = typeof window !== 'undefined'
    ? sessionStorage.getItem(IMPERSONATION_ORIGINAL_TOKEN_KEY)
    : null;

  const [state, setState] = useState<AuthState>({
    ...initialState,
    isImpersonating: restoredIsImpersonating,
    originalToken: restoredOriginalToken,
  });

  // Refs for token refresh
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastRefreshAttemptRef = useRef<number>(0);

  const setAuthenticatedState = useCallback(
    (user: User, token: string, credits: number | null) => {
      const impersonating = getImpersonatorId(token) != null;
      setState({
        user,
        token,
        credits,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        isImpersonating: impersonating,
        originalToken: impersonating
          ? (sessionStorage.getItem(IMPERSONATION_ORIGINAL_TOKEN_KEY) ?? null)
          : null,
      });
    },
    []
  );

  const clearUserScopedClientState = useCallback(async () => {
    clearAllUserScopedCaches();

    await queryClient.cancelQueries({
      predicate: (query) => isUserScopedQuery(query.queryKey),
    });

    queryClient.removeQueries({
      predicate: (query) => isUserScopedQuery(query.queryKey),
    });
  }, [queryClient]);

  const handleAuthFailure = useCallback((errorMessage: string | null = null) => {
    void clearUserScopedClientState();
    authService.clearToken();
    setState({
      user: null,
      token: null,
      credits: null,
      isAuthenticated: false,
      isLoading: false,
      error: errorMessage,
      isImpersonating: false,
      originalToken: null,
    });
  }, [clearUserScopedClientState]);

  /**
   * Attempt to refresh the JWT token silently
   * Called periodically and on visibility/focus change
   */
  const attemptTokenRefresh = useCallback(async () => {
    // Debounce: don't refresh if we just tried
    const now = Date.now();
    if (now - lastRefreshAttemptRef.current < REFRESH_DEBOUNCE_MS) return;
    lastRefreshAttemptRef.current = now;

    const newToken = await authService.refreshToken();

    if (newToken) {
      // Token was refreshed successfully
      setState(prev => ({ ...prev, token: newToken }));
    } else {
      // Check if token was cleared (401 case) - need to logout
      // This matches the existing logout flow: clear user + credits + cached validation
      const currentToken = authService.getToken();
      if (!currentToken && state.isAuthenticated) {
        // Token was cleared by refreshToken() due to 401 (clearToken clears all: token, user, validation timestamp)
        setState({
          user: null,
          token: null,
          credits: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          isImpersonating: false,
          originalToken: null,
        });
      }
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    let isActive = true;

    const init = async () => {
      const token = authService.getToken();
      debugEvent('Auth.init:start', { hasToken: Boolean(token) });
      if (!token) {
        if (!isActive) return;
        setState({ ...initialState, isLoading: false });
        debugEvent('Auth.init:skip-no-token');
        return;
      }

      // OPTIMISTIC: Use cached user data immediately (no loading screen)
      const cachedUser = authService.getUser();
      debugEvent('Auth.init:cached-user', { hasCachedUser: Boolean(cachedUser) });
      if (cachedUser) {
        // Immediately set authenticated state with cached data
        const impersonating = getImpersonatorId(token) != null;
        setState({
          user: cachedUser,
          token,
          credits: null, // Will be fetched in background
          isAuthenticated: true,
          isLoading: false, // No loading screen!
          error: null,
          isImpersonating: impersonating,
          originalToken: impersonating
            ? (sessionStorage.getItem(IMPERSONATION_ORIGINAL_TOKEN_KEY) ?? null)
            : null,
        });

        // Skip Flask validation if:
        // 1. JWT token is not expired (checked locally)
        // 2. Validation cache is still fresh (< 5 min old)
        if (authService.canSkipValidation()) {
          debugEvent('Auth.init:skip-validation');
          // Just fetch credits, skip expensive Flask validation
          const credits = await authService.fetchCredits(token).catch(() => null);
          if (!isActive) return;
          setAuthenticatedState(cachedUser, token, credits);
          debugEvent('Auth.init:done');
          return;
        }

        // Cache expired - do background validation (silent - don't show loading)
        try {
          debugEvent('Auth.init:validate');
          const [freshUser, credits] = await Promise.all([
            authService.validateToken(token),
            authService.fetchCredits(token).catch(() => null),
          ]);

          if (!isActive) return;

          // Update with fresh data
          authService.setUser(freshUser);
          setAuthenticatedState(freshUser, token, credits);
        } catch {
          // Token expired - logout silently
          if (!isActive) return;
          handleAuthFailure(null);
          debugEvent('Auth.init:validate-failed');
        }
        debugEvent('Auth.init:done');
        return;
      }

      // No cached user - must validate (show loading)
      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        debugEvent('Auth.init:validate-no-cache');
        const [user, credits] = await Promise.all([
          authService.validateToken(token),
          authService.fetchCredits(token).catch((err) => {
            console.warn('[AuthContext] Failed to fetch credits during init:', err);
            return null;
          }),
        ]);

        if (!isActive) return;
        authService.setUser(user);
        setAuthenticatedState(user, token, credits);
        debugEvent('Auth.init:done');
      } catch (error) {
        if (!isActive) return;
        const message = error instanceof Error ? error.message : 'Authentication failed';
        handleAuthFailure(message);
        debugEvent('Auth.init:failed', { error: message });
      }
    };

    init();

    return () => {
      isActive = false;
    };
  }, [handleAuthFailure, setAuthenticatedState]);

  // Effect: Periodic token refresh check (every hour)
  useEffect(() => {
    if (!state.isAuthenticated) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Check on auth state change
    attemptTokenRefresh();

    // Set up periodic check
    refreshIntervalRef.current = setInterval(attemptTokenRefresh, REFRESH_CHECK_INTERVAL_MS);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [state.isAuthenticated, attemptTokenRefresh]);

  // Effect: Token refresh on visibility/focus change
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        attemptTokenRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', attemptTokenRefresh);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', attemptTokenRefresh);
    };
  }, [state.isAuthenticated, attemptTokenRefresh]);

  // Effect: keep isImpersonating aligned with the JWT stored in localStorage.
  // bfcache restore (pageshow with persisted=true), tab focus, cross-tab storage
  // writes, and same-tab token swaps (AUTH_TOKEN_CHANGE_EVENT) can all leave
  // React state out of sync with the token. AdminModeBar already reads the JWT
  // directly so its visibility is guaranteed, but downstream consumers that
  // read state.isImpersonating (layout math, route guards) also need to agree.
  useEffect(() => {
    const syncFromStoredToken = () => {
      const token = authService.getToken();
      const impersonating = token ? getImpersonatorId(token) != null : false;
      setState((prev) => {
        if (prev.isImpersonating === impersonating && (!token || prev.token === token)) {
          return prev;
        }
        const nextOriginalToken = impersonating
          ? (sessionStorage.getItem(IMPERSONATION_ORIGINAL_TOKEN_KEY) ?? prev.originalToken)
          : null;
        return {
          ...prev,
          token: token ?? prev.token,
          isImpersonating: impersonating,
          originalToken: nextOriginalToken,
        };
      });
    };

    const onPageShow = () => syncFromStoredToken();
    const onFocus = () => syncFromStoredToken();
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === 'auth_token') {
        syncFromStoredToken();
      }
    };
    const onTokenChange = () => syncFromStoredToken();

    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_TOKEN_CHANGE_EVENT, onTokenChange);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_TOKEN_CHANGE_EVENT, onTokenChange);
    };
  }, []);

  const login = useCallback(
    async (token: string) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        // Fetch user AND credits in PARALLEL (not sequential)
        const [user, credits] = await Promise.all([
          authService.validateToken(token),
          authService.fetchCredits(token).catch((err) => {
            console.warn('[AuthContext] Failed to fetch credits during login:', err);
            return null;
          }),
        ]);

        await clearUserScopedClientState();

        // Store token and user after validation
        authService.setToken(token);
        authService.setUser(user);

        setAuthenticatedState(user, token, credits);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Login failed';
        handleAuthFailure(message);
        throw error;
      }
    },
    [clearUserScopedClientState, handleAuthFailure, setAuthenticatedState]
  );

  const logout = useCallback(() => {
    void clearUserScopedClientState();
    authService.clearToken();
    setState({
      ...initialState,
      isLoading: false,
    });
  }, [clearUserScopedClientState]);

  const refreshCredits = useCallback(async () => {
    const token = authService.getToken();
    if (!token) return;

    try {
      debugEvent('Auth.refreshCredits:start');
      const credits = await authService.fetchCredits();
      setState((prev) => ({ ...prev, credits }));
      debugEvent('Auth.refreshCredits:success', { credits });
    } catch (error) {
      console.warn('[AuthContext] Failed to refresh credits:', error);
      debugEvent('Auth.refreshCredits:error', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  /**
   * Force refresh user data from Flask API
   * Use on premium-sensitive pages like /load-questions and /workbooks
   */
  const refreshUser = useCallback(async () => {
    const token = authService.getToken();
    if (!token) return;

    try {
      debugEvent('Auth.refreshUser:start');
      // Clear cache to force WordPress validation
      authService.clearValidationCache();

      // Fetch fresh user data and credits in parallel
      const [freshUser, credits] = await Promise.all([
        authService.validateToken(token),
        authService.fetchCredits(token).catch(() => null),
      ]);

      // Update cached user and state
      authService.setUser(freshUser);
      setAuthenticatedState(freshUser, token, credits);
      debugEvent('Auth.refreshUser:success');
    } catch (error) {
      console.warn('[AuthContext] Failed to refresh user:', error);
      // Don't logout on refresh failure - keep using cached data
      debugEvent('Auth.refreshUser:error', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [setAuthenticatedState]);

  const updateCredits = useCallback((newCredits: number | null) => {
    setState((prev) => {
      if (prev.credits === newCredits) return prev;
      return { ...prev, credits: newCredits };
    });
  }, []);

  // @MX:ANCHOR: [AUTO] startImpersonation - admin impersonation entry point, called from AdminUsers page
  // @MX:REASON: Public API boundary; modifies auth state and persists impersonation context to sessionStorage
  const startImpersonation = useCallback(async (userId: number) => {
    const currentToken = authService.getToken();
    const currentUser = authService.getUser();

    if (!currentToken || !currentUser) return;

    // Persist original admin credentials and current URL to sessionStorage so they survive page refresh
    sessionStorage.setItem(IMPERSONATION_ORIGINAL_TOKEN_KEY, currentToken);
    sessionStorage.setItem(IMPERSONATION_ORIGINAL_USER_KEY, JSON.stringify(currentUser));
    sessionStorage.setItem(IMPERSONATION_RETURN_URL_KEY, window.location.pathname + window.location.search);

    try {
      const response = await api.post<{ token: string; user: User }>(
        `/api/admin/impersonate/${userId}`
      );

      const { token: impersonatedToken, user: impersonatedUser } = response.data;

      // Clear user-scoped caches before switching to impersonated user.
      // React Query caches workbook lists for 5 minutes, so removing them here
      // prevents the admin's cached data from flashing in the impersonated session.
      await clearUserScopedClientState();

      // Remove stale validation cache so next visit forces fresh validation
      localStorage.removeItem('auth_validated_at');

      // Update auth storage with impersonated user credentials
      authService.setToken(impersonatedToken);
      authService.setUser(impersonatedUser);

      setState((prev) => ({
        ...prev,
        user: impersonatedUser,
        token: impersonatedToken,
        credits: null,
        isAuthenticated: true,
        isImpersonating: true,
        originalToken: currentToken,
      }));

      navigate('/workbooks');
    } catch (error) {
      // Rollback sessionStorage on failure
      sessionStorage.removeItem(IMPERSONATION_ORIGINAL_TOKEN_KEY);
      sessionStorage.removeItem(IMPERSONATION_ORIGINAL_USER_KEY);
      sessionStorage.removeItem(IMPERSONATION_RETURN_URL_KEY);
      console.error('[AuthContext] startImpersonation failed:', error);
      throw error;
    }
  }, [clearUserScopedClientState, navigate]);

  // @MX:ANCHOR: [AUTO] stopImpersonation - admin impersonation exit point, called from AdminModeBar
  // @MX:REASON: Public API boundary; restores admin credentials from sessionStorage or backend
  const stopImpersonation = useCallback(async () => {
    const originalToken = sessionStorage.getItem(IMPERSONATION_ORIGINAL_TOKEN_KEY);
    const originalUserRaw = sessionStorage.getItem(IMPERSONATION_ORIGINAL_USER_KEY);

    // Clean up sessionStorage
    const returnUrl = sessionStorage.getItem(IMPERSONATION_RETURN_URL_KEY) || '/admin/users';
    sessionStorage.removeItem(IMPERSONATION_ORIGINAL_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_ORIGINAL_USER_KEY);
    sessionStorage.removeItem(IMPERSONATION_RETURN_URL_KEY);

    // Clear user-scoped caches before identity switch to prevent
    // impersonated user data from leaking back into the admin account.
    await clearUserScopedClientState();

    if (originalToken && originalUserRaw) {
      // Case 1: Same tab - restore from sessionStorage (fast, sync)
      api.post('/api/admin/impersonate/stop').catch(() => {});

      let originalUser: User | null = null;
      try {
        originalUser = JSON.parse(originalUserRaw) as User;
      } catch {
        originalUser = null;
      }

      authService.setToken(originalToken);
      if (originalUser) authService.setUser(originalUser);
      localStorage.removeItem('auth_validated_at');

      setState((prev) => ({
        ...prev,
        user: originalUser,
        token: originalToken,
        credits: null,
        isAuthenticated: true,
        isImpersonating: false,
        originalToken: null,
      }));
      navigate(returnUrl, { replace: true });
    } else {
      // Case 2: Different tab/browser - get fresh admin credentials from backend
      try {
        const response = await api.post<{ success: boolean; admin_token?: string; admin_user?: User }>(
          '/api/admin/impersonate/stop'
        );
        const { admin_token, admin_user } = response.data;

        if (admin_token && admin_user) {
          authService.setToken(admin_token);
          authService.setUser(admin_user);
          localStorage.removeItem('auth_validated_at');

          setState((prev) => ({
            ...prev,
            user: admin_user,
            token: admin_token,
            credits: null,
            isAuthenticated: true,
            isImpersonating: false,
            originalToken: null,
          }));
          navigate(returnUrl, { replace: true });
        } else {
          // Backend didn't return admin creds - logout
          authService.clearToken();
          setState({ ...initialState, isLoading: false });
          navigate('/login');
        }
      } catch {
        authService.clearToken();
        setState({ ...initialState, isLoading: false });
        navigate('/login');
      }
    }
  }, [clearUserScopedClientState, navigate]);

  const contextValue = useMemo(
    () => ({
      state,
      user: state.user,
      token: state.token,
      credits: state.credits,
      isImpersonating: state.isImpersonating,
      login,
      logout,
      refreshCredits,
      refreshUser,
      updateCredits,
      startImpersonation,
      stopImpersonation,
    }),
    [state, login, logout, refreshCredits, refreshUser, updateCredits, startImpersonation, stopImpersonation]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
